import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { select, Store } from '@ngrx/store';
import { combineLatest, of, Subject } from 'rxjs';
import { switchMap, take, takeUntil } from 'rxjs/operators';

import { ResourceStatus, ResourceType } from '@completion/enums';
import { TreeNode, TreeNodeType } from '@completion/models';
import { State } from '@completion/reducers';
import {
  getCurrentCpId,
  getCurrentMcpId,
  getCurrentProject,
  getCurrentTask,
  getProjectTreeNodes,
  getResourceState,
} from '@completion/selectors';
import { groupBy } from '@completion/utils';
import { CpService, ProjectAccessService, TreeStatusService } from '@completion/services';
import { Router } from '@angular/router';
import { NonCertifiedAssignmentsDialogComponent } from './non-certified-assignments-dialog/non-certified-assignments-dialog.component';

@Component({
  selector: 'app-tree',
  templateUrl: './tree.component.html',
  styleUrls: ['./tree.component.scss'],
  standalone: false,
})
export class TreeComponent implements OnDestroy {
  resourceType = ResourceType.ProjectGetProjectTree;
  nodeType = TreeNodeType;
  treeControl: FlatTreeControl<FakeFlatNode> = new FlatTreeControl<FakeFlatNode>(getNodeLevel, getIsNodeExpandable);
  nodes: MatTreeFlatDataSource<TreeNode, FakeFlatNode>;
  loading: boolean;
  loadingError: string;
  isOpen: boolean;
  displayStatusIcons: boolean;
  hasAccessToSystemView: boolean = false;
  private currentCpId: number | null = null;
  private currentMcpId: number | null = null;

  toggle(node: FakeFlatNode): void {
    this.treeControl.toggle(node);
  }

  toggleAndGoToLink(node: FakeFlatNode): void {
    this.treeControl.toggle(node);
    if (node.nodeType === TreeNodeType.SYSTEM && this.hasAccessToSystemView) {
      const systemId = node.id;
      this.router.navigate(['/system', systemId]);
    }
  }

  private readonly destroy$ = new Subject<boolean>();

  constructor(
    private readonly store: Store<State>,
    private readonly treeStatusService: TreeStatusService,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly cpService: CpService,
    private access: ProjectAccessService,
  ) {
    const treeFlattener = new MatTreeFlattener<TreeNode, FakeFlatNode>(nodeTransformer, getNodeLevel, getIsNodeExpandable, getNodeChildren);

    this.access.isEditor$.pipe(takeUntil(this.destroy$)).subscribe((isEditor) => (this.hasAccessToSystemView = isEditor));

    combineLatest([this.store.select(getProjectTreeNodes), this.store.select(getCurrentTask)])
      .pipe(
        takeUntil(this.destroy$),
        switchMap(([taskNodes, task]) => {
          if (task && task.id) {
            return of(this.toSystemNodes(taskNodes.filter((node) => node.id === task.id)));
          }

          const systemNodes = this.toSystemNodes(taskNodes);
          return of(this.groupNodes(systemNodes));
        }),
      )
      .subscribe((treeNodes) => {
        this.nodes = new MatTreeFlatDataSource(this.treeControl, treeFlattener);
        this.nodes.data = treeNodes;
        // Defer expansion to avoid signal writes during render
        setTimeout(() => {
          this.expandAllSubsystemsSafe();
          this.scheduleExpandForCurrent();
        }, 0);
      });

    this.store
      .select(getCurrentProject)
      .pipe(takeUntil(this.destroy$))
      .subscribe((p) => {
        if (p) {
          const projectConfig = p.config.find((c) => c.name === 'DISPLAY_STATUS_ICONS');
          this.displayStatusIcons = projectConfig?.value === 'true';
        }
      });
    this.store.pipe(select(getResourceState, this.resourceType), takeUntil(this.destroy$)).subscribe((state) => {
      this.loading = state.status === ResourceStatus.InProgress;
      this.loadingError = state.lastError;
    });

    this.store.pipe(select(getCurrentCpId), takeUntil(this.destroy$)).subscribe((id) => {
      if (id) {
        this.currentCpId = id;
        this.scheduleExpandForCurrent();
      }
    });

    this.store.pipe(select(getCurrentMcpId), takeUntil(this.destroy$)).subscribe((id) => {
      if (id) {
        this.currentMcpId = id;
        this.scheduleExpandForCurrent();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
  trackByFn(_index: number, node: any): string {
    return node.id; // Use a unique identifier for each node
  }

  hasChild = (_: number, node: TreeNode) => !!node.children && node.children.length > 0;

  private groupNodes(ungroupedSystemNodes: TreeNode[]): TreeNode[] {
    const systemNumberToSystemsMap = groupBy(ungroupedSystemNodes, 'number');
    const groupedSystems = Object.values(systemNumberToSystemsMap).map((systems: TreeNode[]) => {
      const [firstSystem] = systems;

      return {
        ...firstSystem,
        children: systems.reduce((array, system) => [...array, ...system.children], []),
      };
    });

    groupedSystems.sort(this.sortTreenode);

    return groupedSystems.map((system) => {
      const groupedSubsystem = groupBy(system.children, 'number');
      const children = Object.values(groupedSubsystem).map((subsystems: TreeNode[]) => {
        const [firstSubSystem] = subsystems;

        const cps: TreeNode[] = subsystems.reduce((array, subsystem) => [...array, ...subsystem.children], []);
        cps.sort(this.sortTreenode);

        return {
          ...firstSubSystem,
          children: cps,
        };
      });

      children.sort(this.sortTreenode);

      return {
        ...system,
        children,
      };
    });
  }

  private readonly sortTreenode = (a, b) => {
    if (a.number > b.number) {
      return 1;
    } else {
      return -1;
    }
  };

  // Top level is tasks, but the tree only shows from system level
  private toSystemNodes(treeNodes: TreeNode[]): TreeNode[] {
    return treeNodes.reduce((array: TreeNode[], node: TreeNode) => {
      return [...array, ...node.children];
    }, []);
  }

  private expandNode(nodeId: number, nodeType: TreeNodeType) {
    const foundNodes: FakeFlatNode[] = [];
    for (const itNode of this.nodes.data) {
      let node: FakeFlatNode = this.searchTree(itNode, nodeId, nodeType);

      while (node) {
        foundNodes.push(node);

        node = this.searchTree(itNode, node.parentId, this.getParentNodeType(node.nodeType));
      }
    }
    for (let i = foundNodes.length - 1; i >= 0; i--) {
      this.treeControl.expand(this.treeControl.dataNodes.filter((e) => e.id === foundNodes[i].id)[0]);
    }
  }

  private searchTree(node, compare: number, nodeType: TreeNodeType): any {
    if (node.id === compare && node.nodeType === nodeType) {
      return node;
    } else if (node.children != null) {
      let result = null;
      for (let i = 0; result == null && i < node.children.length; i++) {
        result = this.searchTree(node.children[i], compare, nodeType);
      }
      return result;
    }
    return null;
  }

  private getParentNodeType(currentNodeType: TreeNodeType) {
    switch (currentNodeType) {
      case TreeNodeType.MCP:
        return TreeNodeType.CP;
      case TreeNodeType.CP:
        return TreeNodeType.SUBSYSTEM;
      case TreeNodeType.SUBSYSTEM:
        return TreeNodeType.SYSTEM;
      default:
        return null;
    }
  }

  displayStatusIcon(): boolean {
    return this.displayStatusIcons;
  }

  private normalizeCoverage(coverage?: number): number | null {
    if (coverage == null || Number.isNaN(coverage)) {
      return null;
    }

    const clamped = Math.max(0, coverage);
    // Support both legacy ratio values (0..1) and percentage values (0..100).
    return clamped <= 1 ? clamped * 100 : Math.min(100, clamped);
  }

  coverageToFillPercent(coverage?: number): number {
    const normalized = this.normalizeCoverage(coverage);
    if (normalized == null) {
      return 0;
    }

    if (normalized === 100) {
      return 100;
    }

    return Math.max(0, normalized);
  }

  coverageState(coverage?: number): 'uncovered' | 'partial' | 'full' {
    const normalized = this.normalizeCoverage(coverage);
    if (!normalized || normalized <= 0) {
      return 'uncovered';
    }

    return normalized >= 100 ? 'full' : 'partial';
  }

  coverageLabel(coverage?: number): string {
    const normalized = this.normalizeCoverage(coverage);
    if (normalized == null) {
      return '0.0%';
    }
    return `${normalized.toFixed(1)}%`;
  }

  coverageTooltip(mccCoverage?: number, rfccCoverage?: number, rfocCoverage?: number): string {
    return `MCC ${this.coverageLabel(mccCoverage)} | RFCC ${this.coverageLabel(rfccCoverage)} | RFOC ${this.coverageLabel(rfocCoverage)}`;
  }

  openNonCertifiedAssignmentsDialog(event: Event, node: TreeNode): void {
    event.preventDefault();
    event.stopPropagation();
    if (node.nodeType !== TreeNodeType.CP || !node.id) {
      return;
    }

    this.store
      .select(getCurrentProject)
      .pipe(take(1))
      .subscribe((project) => {
        if (!project?.id) {
          return;
        }

        this.cpService
          .getNonCertifiedCheckSheetAssignments(project.id, node.id)
          .pipe(take(1))
          .subscribe((rows) => {
            const resultRows = rows || [];
            const hasRows = resultRows.length > 0;
            this.dialog.open(NonCertifiedAssignmentsDialogComponent, {
              width: hasRows ? '90vw' : '700px',
              maxWidth: hasRows ? '1400px' : '90vw',
              maxHeight: '90vh',
              data: {
                cpNumber: node.number,
                rows: resultRows,
              },
            });
          });
      });
  }

  private scheduleExpandForCurrent(): void {
    // Ensure nodes and flattened data are ready
    if (!this.nodes || !this.nodes.data || !this.nodes.data.length) {
      return;
    }
    // Defer to next tick to let dataNodes sync
    setTimeout(() => {
      if (!this.treeControl || !this.treeControl.dataNodes || !this.treeControl.dataNodes.length) {
        return;
      }
      if (this.currentCpId) {
        this.expandNode(this.currentCpId, TreeNodeType.CP);
      }
      if (this.currentMcpId) {
        this.expandNode(this.currentMcpId, TreeNodeType.MCP);
      }
    }, 0);
  }

  private expandAllSubsystemsSafe(): void {
    if (!this.treeControl || !this.treeControl.dataNodes || !this.treeControl.dataNodes.length) {
      return;
    }
    const subsystems = this.treeControl.dataNodes.filter((n) => n.nodeType === this.nodeType.SUBSYSTEM);
    subsystems.forEach((n) => this.treeControl.expand(n));
  }
}

const nodeTransformer = (node: TreeNode, level: number) => {
  return {
    ...node,
    level,
    hasChildren: node.children && node.children.length > 0,
  };
};

const getNodeChildren = ({ children }: TreeNode) => {
  return children;
};

const getNodeLevel = ({ level }: FakeFlatNode) => {
  return level;
};

const getIsNodeExpandable = ({ hasChildren }: FakeFlatNode) => {
  return hasChildren;
};

interface FakeFlatNode extends TreeNode {
  level: number;
  hasChildren: boolean;
}
